import { useMsal } from '@azure/msal-react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Spinner,
  Switch,
  mergeClasses,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useCommonStyles, useStores } from '../../../common/hooks';
import { MAX_MASTER_AWARD_TITLE_LENGTH } from '../../../models/schemas/master-award-request.schema';
import { ValidationMessageList } from '../../shared-components';

interface AddOrEditMasterAwardProps {
  isModalOpen: boolean;
  onDismissModal(): void;
}

const AddOrEditMasterAwardModalImpl: React.FC<AddOrEditMasterAwardProps> = (props) => {
  const {
    isModalOpen,
    onDismissModal,
  } = props;

  const {
    instance,
  } = useMsal();

  const {
    apiStore,
    manageAwardListPageUiStore,
  } = useStores();

  const commonStyles = useCommonStyles();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isBeingValidated, setIsBeingValidated] = useState<boolean>(false);

  const createMasterAward = async (): Promise<void> => {
    if (!manageAwardListPageUiStore.masterAwardRequestBody) return;

    try {
      await apiStore.createMasterAward(instance, manageAwardListPageUiStore.masterAwardRequestBody);
    } catch (error) {
      // TODO: handle error
    }
  };

  const updateMasterAward = async (masterAwardId: number): Promise<void> => {
    if (!manageAwardListPageUiStore.masterAwardRequestBody) return;

    try {
      await apiStore.updateMasterAward(instance, masterAwardId, manageAwardListPageUiStore.masterAwardRequestBody);
    } catch (error) {
      // TODO: handle error
    }
  };

   const getMasterAwards = async (): Promise<void> => {
    try {
      const masterAwards = await apiStore.getMasterAwards(instance);
      manageAwardListPageUiStore.setMasterAwards(masterAwards);
    } catch (error) {
      // TODO: handle error
    }
  };


  const handleDismissModal = (): void => {
    setIsBeingValidated(false);
    manageAwardListPageUiStore.setSelectedMasterAwardId(undefined);
    onDismissModal();
  };

  const handleSave = async (): Promise<void> => {
    setIsBeingValidated(true);

    if (!manageAwardListPageUiStore.masterAwardRequestValidation.isValid) return;

    setIsLoading(true);

    if (manageAwardListPageUiStore.addOrEditMasterAwardModalType === 'add') {
      await createMasterAward();
    } else if (manageAwardListPageUiStore.addOrEditMasterAwardModalType === 'update' && manageAwardListPageUiStore.selectedMasterAwardId) {
      await updateMasterAward(manageAwardListPageUiStore.selectedMasterAwardId);
    }

    await getMasterAwards();

    setIsLoading(false);
    onDismissModal();
  };

  return (
    <Dialog
      open={isModalOpen}
      onOpenChange={(_, data) => {
        if (!data.open) handleDismissModal();
      }}
    >
      <DialogSurface
        className={mergeClasses(commonStyles.modalSurface, 'add-or-edit-master-award-modal')}
      >
        <DialogBody
          className={commonStyles.modalBody}
        >
          <DialogTitle
            action={
              <Button
                appearance='subtle'
                aria-label='close'
                icon={<Dismiss24Regular />}
                onClick={() => handleDismissModal()}
              />
            }
          >
            {
              manageAwardListPageUiStore.addOrEditMasterAwardModalType === 'add' && 'Add Master Award'
            }
            {
              manageAwardListPageUiStore.addOrEditMasterAwardModalType === 'update' && 'Update Master Award'
            }
          </DialogTitle>
          <DialogContent>
            {
              isLoading ? (
                <Spinner
                  label='Loading...'
                />
              ) : (
                <>
                  <Field
                    required
                    label={{
                      children: 'Title',
                      className: commonStyles.inputFieldLabel,
                    }}
                    validationMessage={
                      isBeingValidated
                      && manageAwardListPageUiStore.masterAwardRequestValidation.fieldErrors.title?.length
                        ? (
                          <ValidationMessageList
                            messages={manageAwardListPageUiStore.masterAwardRequestValidation.fieldErrors.title}
                          />
                        )
                        : undefined
                    }
                    validationMessageIcon={null}
                    hint={`${manageAwardListPageUiStore.masterAwardRequestBody?.title.length || 0}/${MAX_MASTER_AWARD_TITLE_LENGTH}`}
                  >
                    <Input
                      value={manageAwardListPageUiStore.masterAwardRequestBody?.title || ''}
                      className={commonStyles.inputField}
                      maxLength={MAX_MASTER_AWARD_TITLE_LENGTH}
                      onChange={(_, data) => manageAwardListPageUiStore.setMasterAwardRequestBody({ key: 'title', value: data.value })}
                    />
                  </Field>
                  <Field
                    validationMessage={
                      isBeingValidated
                      && manageAwardListPageUiStore.masterAwardRequestValidation.fieldErrors.isActive?.length
                        ? (
                          <ValidationMessageList
                            messages={manageAwardListPageUiStore.masterAwardRequestValidation.fieldErrors.isActive}
                          />
                        )
                        : undefined
                    }
                    validationMessageIcon={null}
                  >
                    <Switch
                      label='Active'
                      checked={manageAwardListPageUiStore.masterAwardRequestBody?.isActive ?? false}
                      onChange={(_, data) => manageAwardListPageUiStore.setMasterAwardRequestBody({ key: 'isActive', value: data.checked })}
                    />
                  </Field>
                </>
              )
            }
          </DialogContent>
          <DialogActions>
            <Button
              appearance='secondary'
              onClick={() => handleDismissModal()}
              aria-label='Back button'
              title='Back'
            >
              Back
            </Button>
            <Button
              appearance='primary'
              disabled={isLoading || (isBeingValidated && !manageAwardListPageUiStore.masterAwardRequestValidation.isValid)}
              onClick={() => handleSave()}
              aria-label='save and continue button'
              title='Save and continue'
            >
              {
                manageAwardListPageUiStore.addOrEditMasterAwardModalType === 'add' && 'Add'
              }
              {
                manageAwardListPageUiStore.addOrEditMasterAwardModalType === 'update' && 'Update'
              }
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export const AddOrEditMasterAwardModal = observer(AddOrEditMasterAwardModalImpl);
